"use client";

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  setNotifications,
  setUnreadCount,
  setPermissionStatus,
  setLoading,
  markRead as markReadAction,
  markAllRead as markAllReadAction,
  removeNotification as removeNotificationAction,
  clearAll as clearAllAction,
} from '../redux/slices/notificationsSlice';
import {
  getNotifications as fetchNotifications,
  getUnreadCount as fetchUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  clearAllNotifications as clearAllApi,
} from '../services/notificationService';
import { makeApiCall } from '@/lib/apiUtils';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user);
  const { unreadCount, permissionStatus } = useSelector((state) => state.notifications);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [oneSignalAppId, setOneSignalAppId] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const intervalRef = useRef(null);
  const sdkLoadedRef = useRef(false);

  const userRole = currentUser?.role || 'member';

  // Fetch unread count
  const refreshUnreadCount = useCallback(async () => {
    if (!currentUser) return;
    try {
      const result = await fetchUnreadCount(userRole);
      if (result?.success) {
        dispatch(setUnreadCount(result.unreadCount));
      }
    } catch (error) {
      // Silent fail
    }
  }, [currentUser, userRole, dispatch]);

  // Fetch notifications
  const loadNotifications = useCallback(async (params = {}) => {
    if (!currentUser) return;
    dispatch(setLoading(true));
    try {
      const result = await fetchNotifications({ ...params, role: userRole });
      if (result?.success) {
        dispatch(setNotifications(result));
      }
    } catch (error) {
      // Silent fail
    }
  }, [currentUser, userRole, dispatch]);

  // Mark as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      dispatch(markReadAction(notificationId));
      await markNotificationAsRead(notificationId, userRole);
    } catch (error) {
      // Silent fail
    }
  }, [userRole, dispatch]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      dispatch(markAllReadAction());
      await markAllNotificationsAsRead(userRole);
    } catch (error) {
      // Silent fail
    }
  }, [userRole, dispatch]);

  // Delete notification
  const removeNotification = useCallback(async (notificationId) => {
    try {
      dispatch(removeNotificationAction(notificationId));
      await deleteNotification(notificationId, userRole);
    } catch (error) {
      // Silent fail
    }
  }, [userRole, dispatch]);

  // Clear all
  const clearAll = useCallback(async () => {
    try {
      dispatch(clearAllAction());
      await clearAllApi(userRole);
    } catch (error) {
      // Silent fail
    }
  }, [userRole, dispatch]);

  // Load OneSignal SDK dynamically (avoids hydration issues)
  const loadOneSignalSDK = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (sdkLoadedRef.current) return;
    if (document.getElementById('onesignal-sdk')) return;

    console.log('🔔 [OneSignal] Loading SDK script...');

    const script = document.createElement('script');
    script.id = 'onesignal-sdk';
    script.src = 'https://cdn.onesignal.com/sdks/OneSignalSDK.js';
    script.async = true;
    script.onload = () => {
      console.log('✅ [OneSignal] SDK script loaded');
      sdkLoadedRef.current = true;
      // Don't call initializeOneSignal here - let useEffect handle it
      // This forces a re-render to trigger the useEffect
      setSdkReady(false); // trigger re-render
    };
    document.head.appendChild(script);
  }, []);

  // Initialize OneSignal
  const initializeOneSignal = useCallback(() => {
    const onesignal = window.OneSignal;
    if (!onesignal) {
      console.log('❌ [OneSignal] SDK not found on window');
      return;
    }
    if (!oneSignalAppId) {
      console.log('❌ [OneSignal] App ID not available yet');
      return;
    }

    console.log('🔔 [OneSignal] Initializing with App ID:', oneSignalAppId.substring(0, 10) + '...');

    try {
      onesignal.init({
        appId: oneSignalAppId,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
      }).then(() => {
        console.log('✅ [OneSignal] Init successful!');
        setSdkReady(true);

        if (currentUser?.username) {
          console.log('🔔 [OneSignal] Setting external user ID:', currentUser.username);
          onesignal.setExternalUserId(currentUser.username);
        }

        onesignal.getNotificationPermission().then((permission) => {
          console.log('🔔 [OneSignal] Current permission:', permission);
          dispatch(setPermissionStatus(permission));
          if (permission === 'granted') {
            setPushEnabled(true);
          }
        });
      }).catch((error) => {
        console.error('❌ [OneSignal] Init failed:', error.message || error);
      });
    } catch (error) {
      console.error('❌ [OneSignal] Init error:', error.message || error);
    }
  }, [oneSignalAppId, currentUser, dispatch]);

  // Fetch OneSignal config from API
  useEffect(() => {
    if (!currentUser) return;

    const fetchConfig = async () => {
      try {
        const endpoints = [
          '/api/admin/notifications/onesignal-config',
          '/api/website-user/notifications/onesignal-config',
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await makeApiCall(endpoint, { method: 'GET' });
            if (response?.success && response?.data?.enabled && response?.data?.appId) {
              setOneSignalAppId(response.data.appId);
              return;
            }
          } catch (e) {
            continue;
          }
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchConfig();
  }, [currentUser]);

  // Load SDK when appId is available
  useEffect(() => {
    if (oneSignalAppId && !sdkLoadedRef.current) {
      loadOneSignalSDK();
    }
  }, [oneSignalAppId, loadOneSignalSDK]);

  // Initialize when SDK loads and appId available
  useEffect(() => {
    console.log('🔔 [OneSignal] Check init:', {
      sdkLoaded: sdkLoadedRef.current,
      hasAppId: !!oneSignalAppId,
      hasOneSignal: !!window.OneSignal,
      sdkReady
    });

    if (!sdkLoadedRef.current || !oneSignalAppId || sdkReady) return;
    if (typeof window === 'undefined' || !window.OneSignal) return;

    const onesignal = window.OneSignal;

    console.log('🔔 [OneSignal] Initializing with App ID:', oneSignalAppId.substring(0, 10) + '...');

    try {
      onesignal.init({
        appId: oneSignalAppId,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
      }).then(() => {
        console.log('✅ [OneSignal] Init successful!');
        setSdkReady(true);

        if (currentUser?.username) {
          console.log('🔔 [OneSignal] Setting external user ID:', currentUser.username);
          onesignal.setExternalUserId(currentUser.username);
        }

        onesignal.getNotificationPermission().then((permission) => {
          console.log('🔔 [OneSignal] Current permission:', permission);
          dispatch(setPermissionStatus(permission));
          if (permission === 'granted') {
            setPushEnabled(true);
          }
        });
      }).catch((error) => {
        console.error('❌ [OneSignal] Init failed:', error.message || error);
      });
    } catch (error) {
      console.error('❌ [OneSignal] Init error:', error.message || error);
    }
  }, [oneSignalAppId, sdkReady, currentUser, dispatch]);

  // Request push permission and subscribe
  const requestPushPermission = useCallback(async () => {
    const onesignal = window.OneSignal;

    if (!onesignal) {
      toast.error('OneSignal SDK not loaded. Please refresh the page.');
      return { granted: false, error: 'SDK not loaded' };
    }

    if (!oneSignalAppId) {
      toast.error('OneSignal App ID not configured');
      return { granted: false, error: 'App ID not configured' };
    }

    console.log('🔔 [PUSH] Step 1: Initializing OneSignal...');

    // Initialize OneSignal with timeout
    try {
      const initPromise = onesignal.init({
        appId: oneSignalAppId,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Init timeout')), 5000)
      );

      await Promise.race([initPromise, timeoutPromise]);
      console.log('✅ [PUSH] OneSignal initialized');
      setSdkReady(true);
    } catch (initError) {
      // Might already be initialized or timeout, continue anyway
      console.log('🔔 [PUSH] Init result:', initError?.message || 'already initialized');
    }

    console.log('🔔 [PUSH] Step 2: Requesting permission...');

    try {
      const currentPermission = await onesignal.getNotificationPermission();
      console.log('🔔 [PUSH] Current permission:', currentPermission);

      if (currentPermission === 'default') {
        console.log('🔔 [PUSH] Step 3: Showing browser prompt...');
        await onesignal.showNativePrompt();
        await new Promise(r => setTimeout(r, 1000));
      }

      const newPermission = await onesignal.getNotificationPermission();
      console.log('🔔 [PUSH] New permission:', newPermission);

      if (newPermission === 'granted') {
        dispatch(setPermissionStatus('granted'));

        console.log('🔔 [PUSH] Step 4: Getting subscription ID...');
        const subscriptionId = await onesignal.getUserId();
        console.log('🔔 [PUSH] Subscription ID:', subscriptionId);

        if (subscriptionId) {
          console.log('📤 [PUSH] Step 5: Saving to backend...');

          const baseUrl = userRole === 'admin'
            ? '/api/admin/notifications'
            : '/api/website-user/notifications';

          const response = await makeApiCall(`${baseUrl}/save-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscriptionId }),
          });

          if (response?.success) {
            console.log('✅ [PUSH] Step 6: Saved to users collection!');
            console.log('🎉 [PUSH] Complete!');
          }
        }

        setPushEnabled(true);
        toast.success('Push notifications enabled!');
        return { granted: true, subscriptionId };
      } else {
        dispatch(setPermissionStatus('denied'));
        toast.error('Permission denied');
        return { granted: false, error: 'Permission denied' };
      }
    } catch (error) {
      console.error('❌ [PUSH] Error:', error.message);
      toast.error('Failed to enable push notifications');
      return { granted: false, error: error.message };
    }
  }, [userRole, oneSignalAppId, dispatch]);

  // Unregister push
  const unregisterPush = useCallback(async () => {
    try {
      const onesignal = window.OneSignal;
      if (onesignal) {
        const subscriptionId = await onesignal.getUserId();
        if (subscriptionId) {
          const baseUrl = userRole === 'admin'
            ? '/api/admin/notifications'
            : '/api/website-user/notifications';

          await makeApiCall(`${baseUrl}/remove-subscription`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscriptionId }),
          });
        }
      }
      setPushEnabled(false);
    } catch (error) {
      // Silent fail
    }
  }, [userRole]);

  // Initialize on user login
  useEffect(() => {
    if (!currentUser) {
      setIsInitialized(false);
      setPushEnabled(false);
      return;
    }

    if (isInitialized) return;

    const init = async () => {
      await refreshUnreadCount();
      setIsInitialized(true);
    };

    init();

    intervalRef.current = setInterval(refreshUnreadCount, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    unreadCount,
    permissionStatus,
    pushEnabled,
    sdkReady,
    loadNotifications,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    requestPushPermission,
    unregisterPush,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
